import React, { useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import MUIRichTextEditor from 'mui-rte';
import { createTheme } from '@mui/material/styles';
import { ThemeProvider } from '@mui/styles';
import { Buffer } from 'buffer';
import { APIURL } from '../../config';

const notesTheme = createTheme();

const richTextValue = (value) => {
    try {
        if (Array.isArray(JSON.parse(value)?.blocks)) return value;
    } catch (_) { /* Plain text notes are also supported. */ }
    return JSON.stringify({
        blocks: [{ key: 'note', text: value || '', type: 'unstyled', depth: 0, inlineStyleRanges: [], entityRanges: [], data: {} }],
        entityMap: {},
    });
};

function AudioNote({ note }) {
    const [src, setSrc] = useState(null);
    useEffect(() => {
        const data = note.audioNote?.[0]?.audio_note;
        if (!data) return;
        const bytes = data.type === 'Buffer' ? new Uint8Array(data.data) : Buffer.from(data, 'base64');
        const url = URL.createObjectURL(new Blob([bytes], { type: 'audio/wav' }));
        setSrc(url);
        return () => URL.revokeObjectURL(url);
    }, [note]);
    return src ? <audio controls src={src} aria-label={note.note_title || 'Voice note'} /> : null;
}

export default function ContactNotes({ contact, user }) {
    const [result, setResult] = useState({ contactId: null, notes: [], loading: true, error: false });
    const contactId = contact.con_id;
    const companyId = user.comp_id;
    const employeeId = user.emp_id;

    useEffect(() => {
        let active = true;
        setResult({ contactId, notes: [], loading: true, error: false });
        const load = async () => {
            try {
                const params = { contact_id: contactId, company_id: companyId, empID: employeeId };
                const [comments, audio] = await Promise.all([
                    axios.get(APIURL + '/comments', { params }),
                    axios.get(APIURL + '/audio_note', { params }),
                ]);
                const details = await Promise.all(comments.data.map(async (note) => {
                    const response = await axios.get(APIURL + '/comments/details', {
                        params: { comment_id: note.comment_id, empID: employeeId },
                    });
                    return response.data[0];
                }));
                const notes = [
                    ...details.filter(Boolean).map(note => ({ ...note, key: `comment-${note.comment_id}` })),
                    ...audio.data.map(note => ({ ...note, key: `audio-${note.id}`, created_at: note.submitted })),
                ];
                const uniqueNotes = [...new Map(notes.map(note => [note.key, note])).values()];
                uniqueNotes.sort((a, b) => moment(b.created_at).valueOf() - moment(a.created_at).valueOf());
                if (active) setResult({ contactId, notes: uniqueNotes, loading: false, error: false });
            } catch (_) {
                if (active) setResult({ contactId, notes: [], loading: false, error: true });
            }
        };
        load();
        return () => { active = false; };
    }, [contactId, companyId, employeeId]);

    const loading = result.loading || result.contactId !== contactId;
    return (
        <>
            {loading && <CircularProgress size={24} aria-label="Loading contact notes" />}
            {!loading && result.error && <Alert severity="error">Could not load associated notes. Please try again.</Alert>}
            {!loading && !result.error && result.notes.length === 0 && <Typography>No notes available.</Typography>}
            {!loading && result.notes.map(note => (
                <Box key={note.key} sx={{ borderTop: '1px solid #e0e0e0', py: 2 }}>
                    <Typography variant="subtitle2">{note.note_title || note.tas_title || note.pro_title || note.cus_name || 'Note'}</Typography>
                    {note.cus_name && <Typography variant="body2" color="text.secondary">{note.cus_name}</Typography>}
                    <Typography variant="caption" color="text.secondary">
                        {moment(note.created_at).format('MM/DD/YYYY hh:mm A')}
                        {note.emp_first_name && ` · ${note.emp_first_name} ${note.emp_last_name || ''}`}
                    </Typography>
                    {note.audioNote ? <AudioNote note={note} /> : (
                        <ThemeProvider theme={notesTheme}>
                            <MUIRichTextEditor defaultValue={richTextValue(note.comment_text)} readOnly toolbar={false} />
                        </ThemeProvider>
                    )}
                </Box>
            ))}
        </>
    );
}
